import React, { useEffect, useState, useRef, useCallback } from "react";
import {
    Box, Typography, Skeleton, Fade, Accordion, AccordionSummary, AccordionDetails, Container
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import dayjs from "dayjs";
import parse from "html-react-parser";
import PrizesCarousel from "../../components/prizesCarousel";
import io from "socket.io-client";
import PrizeWonCard from "./prize";
import { useDispatch, useSelector } from 'react-redux'
import { useWindowSize } from 'react-use'
import Confetti from 'react-confetti'
import { useNavigate } from "react-router-dom";

const SOCKET_URL = "http://localhost:4000"; // <- Move URL to constant

const RafflePage = () => {
    const dispatch = useDispatch();
    const state = useSelector((state) => state)
    const { width, height } = useWindowSize()
    const navigate = useNavigate();

    // --- State ---
    const [raffle, setRaffle] = useState(null);
    const [loading, setLoading] = useState(true);
    const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
    const [prizeWon, setPrizeWon] = useState(null);

    const socketRef = useRef(null);

    // --- Socket Connection ---
    useEffect(() => {
        console.log("Connecting to socket...");
        socketRef.current = io(SOCKET_URL, { transports: ["websocket"] });

        socketRef.current.emit("joinRaffle", { tk: "Bearer " + state.user.token });

        socketRef.current.on("raffleData", ({ raffle, prize, error }) => {
            if (error) {
                navigate("/")
            }
            if (raffle) {
                setRaffle(raffle);
                setLoading(false);
            }
            if (prize) {
                setPrizeWon(prize);
            }
        });

        socketRef.current.on("drawResult", (data) => {
            console.log("drawResult", data);

            if (data?.prize) {
                // The participant has won a prize
                setPrizeWon(data?.prize);
            } else {
                // No prize
                setPrizeWon("noprize");
            }
        });

        socketRef.current.on("disconnect", () => {
            console.log("Disconnected from socket");
        });

        return () => {
            console.log("Cleaning up socket...");
            socketRef.current.disconnect();
        };
    }, []);

    // --- Countdown Timer ---
    useEffect(() => {
        if (!raffle || !raffle?.drawDate) return;

        const drawDate = dayjs(raffle?.drawDate).valueOf();

        const interval = setInterval(() => {
            const now = dayjs().valueOf();
            const diff = drawDate - now;

            if (diff <= 0) {
                clearInterval(interval);
                setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
                return;
            }

            const days = Math.floor(diff / (1000 * 60 * 60 * 24));
            const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
            const minutes = Math.floor((diff / (1000 * 60)) % 60);
            const seconds = Math.floor((diff / 1000) % 60);

            setTimeLeft({ days, hours, minutes, seconds });
        }, 1000);

        return () => clearInterval(interval);
    }, [raffle]);

    // This means that there are no ongoing raffles
    if (raffle === "NA") {
        return (
            <Box sx={{ mb: 20 }}>
                <Container maxWidth="md" sx={{ textAlign: "center", mt: 2 }}>
                    <Typography sx={{ textAlign: "left", mt: 2 }} variant="h4" fontWeight="bold" mb={2}>
                        Raffle
                    </Typography>
                </Container>
                <Box sx={[{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }, styles.container]}>
                    <Typography variant="h6" fontWeight="bold" mt={20}>
                        Raffle coming soon!
                    </Typography>
                    <Typography variant="p" >
                        Stay tuned for updates
                    </Typography>
                </Box>
            </Box>
        )
    }

    // --- UI ---
    return (
        <Box sx={{ mb: 20 }}>

            <Container maxWidth="md" sx={{ textAlign: "center", mt: 2 }}>
                <Typography sx={{ textAlign: "left", mt: 2 }} variant="h4" fontWeight="bold" mb={2}>
                    Raffle
                </Typography>
            </Container>
            <Confetti
                numberOfPieces={(prizeWon && prizeWon !== "noprize") ? 100 : 0}
                width={width - 20}
                height={height}
            />

            <Container maxWidth="md" sx={{ mt: 2 }}>

                {/* <Box sx={styles.container}> */}
                {/* Title */}
                {loading ? (
                    <Skeleton variant="text" width="80%" height={40} sx={{ margin: "auto" }} />
                ) : (
                    <Fade in timeout={500}>
                        <Typography sx={{ textAlign: "center", mt: 5 }} variant="h5" mb={2}>
                            {raffle?.title}
                        </Typography>
                    </Fade>
                )}

                {/* <button onClick={handleManualDraw} style={{ marginTop: '10px' }}>
                    🎯 Perform Manual Draw
                </button> */}

                {/* Timer */}
                <Box sx={styles.timer}>
                    {loading ? (
                        [...Array(4)].map((_, index) => (
                            <Box sx={styles.timeBox} key={index}>
                                <Skeleton variant="text" width={50} height={50} />
                                <Skeleton variant="text" width={30} height={20} />
                            </Box>
                        ))
                    ) : (
                        <Fade in timeout={500}>
                            <Box sx={styles.timer}>
                                {["Days", "Hours", "Mins", "Sec"].map((label, index) => (
                                    <React.Fragment key={index}>
                                        <Box sx={styles.timeBox}>
                                            <Typography variant="h2" sx={styles.number}>
                                                {Object.values(timeLeft)[index].toString().padStart(2, "0")}
                                            </Typography>
                                            <Typography variant="body2" sx={styles.label}>
                                                {label}
                                            </Typography>
                                        </Box>
                                        {index < 3 && <Typography variant="h2" sx={styles.colon}>:</Typography>}
                                    </React.Fragment>
                                ))}
                            </Box>
                        </Fade>
                    )}
                </Box>

                <PrizeWonCard prizeWon={prizeWon} onLearnHowToClaim={() => { }} />

                {(!prizeWon || prizeWon === "noprize") &&
                    <>
                        {/* Description */}
                        {loading ? (
                            <Skeleton variant="text" width="90%" height={20} sx={{ margin: "auto" }} />
                        ) : (
                            <Fade in timeout={500}>
                                <Typography variant="body1" textAlign="center" mt={1} mb={5} color="gray">
                                    {raffle?.description}
                                </Typography>
                            </Fade>
                        )}


                        {/* Prizes Section */}
                        <Typography variant="h6" fontWeight="bold" mt={3} mb={1}>
                            Get a chance to win
                        </Typography>

                        {loading ? (
                            <Skeleton variant="rectangular" width="100%" height={200} sx={{ borderRadius: 2, mt: 1 }} />
                        ) : (
                            <Fade in timeout={500}>
                                <Box>
                                    {raffle?.prizes?.length > 0 && <PrizesCarousel prizes={raffle?.prizes} />}
                                </Box>
                            </Fade>
                        )}

                        {/* Mechanics */}
                        {loading ? (
                            <Skeleton variant="text" width="90%" height={20} sx={{ margin: "auto", mt: 2 }} />
                        ) : (
                            <Fade in timeout={500}>
                                <Typography mt={3} variant="body2">{parse(raffle?.mechanics)}</Typography>
                            </Fade>
                        )}

                        {/* FAQ Section */}
                        <Typography variant="h6" fontWeight="bold" mt={3}>FAQ</Typography>

                        {loading ? (
                            [...Array(3)].map((_, index) => (
                                <Skeleton key={index} variant="rectangular" width="100%" height={50} sx={{ borderRadius: 2, mt: 1 }} />
                            ))
                        ) : (
                            <Fade in timeout={500}>
                                <Box>
                                    {raffle?.faq?.map((item) => (
                                        <Accordion key={item._id} sx={styles.accordion}>
                                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                                <Typography fontWeight="bold">{item.title}</Typography>
                                            </AccordionSummary>
                                            <AccordionDetails>
                                                <Typography variant="body2" color="gray">
                                                    {item.description}
                                                </Typography>
                                            </AccordionDetails>
                                        </Accordion>
                                    ))}
                                </Box>
                            </Fade>
                        )}
                    </>
                }
                {/* </Box> */}
            </Container>
        </Box>
    );
};

export default RafflePage;

const styles = {
    container: {
        width: "90%",
        maxWidth: 400,
        margin: "auto",
        textAlign: "left",
        // padding: 10,
        paddingBottom: "50px",
    },
    timer: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
    timeBox: {
        textAlign: "center",
        minWidth: "40px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
    },
    number: {
        lineHeight: 1,
    },
    label: {
        fontSize: "12px",
        color: "gray",
    },
    colon: {
        mx: 1,
        mb: 2,
        display: "flex",
        alignItems: "center",
    },
    accordion: {
        mt: 1,
        borderRadius: "8px",
        "&::before": { display: "none" },
        boxShadow: "0 2px 10px rgba(0, 0, 0, 0.2)",
    },
};
